/**
 * The sidebar (NFR-07): sub-menus from the route table, plus the cabinet tree under
 * "Cabinets" (only cabinets the user has a grant on — the API filters them). Desktop:
 * collapsible to icons (remembered). Phones: an off-canvas drawer from the top bar.
 */
import { ChevronDown, ChevronsLeft, ChevronsRight, Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { CabinetTree, cabinetNodeId, cx, folderNodeId } from '../../components/ui';
import { BRAND } from '../../config/brand';
import { useAuth } from '../../lib/auth';
import { useInstallPrompt } from '../../lib/pwa';
import { useCabinets } from '../../lib/queries';
import { GROUPS, NAV_ORDER, ROUTES, routeFor, type GroupId, type RouteDef } from '../routes';
import { Wordmark } from './Wordmark';
import { EsignNavBadge } from '../pages/esign/NavBadge';
import { useAiStatus } from '../pages/ai/api';

const OPEN_KEY = 'dms.nav.open';
const isGroup = (x: string): x is GroupId => GROUPS.some((g) => g.id === x);

const itemCls = (active: boolean) =>
  cx('group relative flex min-h-touch items-center gap-3 rounded-tag px-3 text-[15px] transition-colors', active ? 'bg-rail-hover font-semibold text-rail-ink' : 'text-rail-muted hover:bg-rail-hover/70 hover:text-rail-ink');

/** The active marker is the catalogue card's red rule, turned upright. */
const ActiveRule = () => <span aria-hidden className="absolute bottom-2.5 left-0 top-2.5 w-[3px] rounded-r bg-rule" />;

export function Sidebar({ collapsed, onToggleCollapse, onNavigate }: { collapsed: boolean; onToggleCollapse?: () => void; onNavigate?: () => void }) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const { canInstall, install } = useInstallPrompt();
  const ai = useAiStatus();
  // Q11: AI entries only while the capability is switched on.
  const visible = ROUTES.filter((r) => r.nav && can(...r.perm) && (!r.feature || !!ai.data?.gapCheck));
  const activeGroup = routeFor(pathname)?.nav;
  const [open, setOpen] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(OPEN_KEY) ?? '["documents","cabinets"]') as string[];
    } catch {
      return ['documents', 'cabinets'];
    }
  });
  useEffect(() => {
    if (activeGroup && activeGroup !== 'top' && !open.includes(activeGroup)) setOpen((o) => [...o, activeGroup]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup]);
  const toggle = (g: string) => {
    const next = open.includes(g) ? open.filter((x) => x !== g) : [...open, g];
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const leaf = (r: RouteDef, nested: boolean) => (
    <NavLink key={r.path} to={r.path} end={r.end || r.path === '/'} onClick={onNavigate} title={collapsed ? r.title : undefined} className={({ isActive }) => cx(itemCls(isActive), nested && !collapsed && 'pl-11 text-sm')}>
      {({ isActive }) => (
        <>
          {isActive && <ActiveRule />}
          {(!nested || collapsed) && <r.icon aria-hidden className="h-[18px] w-[18px] shrink-0" />}
          {!collapsed && <span className="truncate">{r.title}</span>}
          {r.badge && <EsignNavBadge kind={r.badge === 'esign-action' ? 'action' : 'returned'} collapsed={collapsed} />}
        </>
      )}
    </NavLink>
  );

  const groupHeader = (id: string, label: string, Icon: RouteDef['icon'], isOpen: boolean, hasActive: boolean) => (
    <button type="button" aria-expanded={isOpen} onClick={() => toggle(id)} className={cx('flex min-h-touch w-full items-center gap-3 rounded-tag px-3 text-[15px] transition-colors hover:bg-rail-hover/70', hasActive ? 'text-rail-ink' : 'text-rail-muted hover:text-rail-ink')}>
      <Icon aria-hidden className="h-[18px] w-[18px] shrink-0" />
      <span className="flex-1 text-left">{label}</span>
      <ChevronDown aria-hidden className={cx('h-4 w-4 transition-transform', isOpen ? 'rotate-0' : '-rotate-90')} />
    </button>
  );

  /** Nested sub-menu (e.g. Records › Physical): a small collapsible header, its entries indented once more. */
  const subMenu = (group: string, rs: RouteDef[]) => {
    const key = `${group}.${rs[0].sub}`;
    const hasActive = rs.some((r) => routeFor(pathname)?.path === r.path || pathname.startsWith(`${r.path}/`));
    const isOpen = collapsed || open.includes(key) || hasActive;
    return (
      <div key={key}>
        {!collapsed && (
          <button type="button" aria-expanded={isOpen} onClick={() => toggle(key)} className={cx('flex min-h-touch w-full items-center gap-2 rounded-tag pl-11 pr-3 text-sm transition-colors hover:bg-rail-hover/70', hasActive ? 'text-rail-ink' : 'text-rail-muted hover:text-rail-ink')}>
            <span className="flex-1 text-left">{rs[0].sub}</span>
            <ChevronDown aria-hidden className={cx('h-4 w-4 transition-transform', isOpen ? 'rotate-0' : '-rotate-90')} />
          </button>
        )}
        {isOpen && <div className={cx('space-y-0.5', !collapsed && 'pl-4')}>{rs.map((r) => leaf(r, true))}</div>}
      </div>
    );
  };

  return (
    <nav aria-label="Main" className="flex h-full flex-col border-r border-rail-line bg-rail text-rail-ink">
      <div className={cx('flex h-16 shrink-0 items-center border-b border-rail-line', collapsed ? 'justify-center px-2' : 'px-4')}>
        <Wordmark compact={collapsed} />
      </div>
      <div className="scrollbar-thin flex-1 space-y-0.5 overflow-y-auto p-2">
        {NAV_ORDER.map((x) => {
          if (x === '/cabinets') {
            const r = visible.find((v) => v.path === x);
            if (!r) return null;
            if (collapsed) return leaf(r, false);
            const isOpen = open.includes('cabinets');
            return (
              <div key="cabinets">
                <div className="flex items-center">
                  <NavLink to="/cabinets" end onClick={onNavigate} className={({ isActive }) => cx(itemCls(isActive), 'flex-1')}>
                    {({ isActive }) => (
                      <>
                        {isActive && <ActiveRule />}
                        <r.icon aria-hidden className="h-[18px] w-[18px] shrink-0" />
                        <span className="truncate">{r.title}</span>
                      </>
                    )}
                  </NavLink>
                  <button type="button" aria-expanded={isOpen} aria-label={isOpen ? 'Hide cabinet folders' : 'Show cabinet folders'} onClick={() => toggle('cabinets')} className="grid h-11 w-10 shrink-0 place-items-center rounded-tag text-rail-muted hover:bg-rail-hover/70 hover:text-rail-ink">
                    <ChevronDown aria-hidden className={cx('h-4 w-4 transition-transform', isOpen ? 'rotate-0' : '-rotate-90')} />
                  </button>
                </div>
                {isOpen && <SidebarCabinets onNavigate={onNavigate} />}
              </div>
            );
          }
          if (!isGroup(x)) {
            const r = visible.find((v) => v.path === x && v.nav === 'top');
            return r ? leaf(r, false) : null;
          }
          const g = GROUPS.find((gg) => gg.id === x)!;
          const kids = visible.filter((r) => r.nav === x);
          if (!kids.length) return null;
          const isOpen = open.includes(x) || collapsed;
          return (
            <div key={x} className={cx(collapsed && 'border-t border-rail-line pt-1')}>
              {!collapsed && groupHeader(x, g.label, g.icon, isOpen, activeGroup === x)}
              {isOpen && <div className="space-y-0.5">{withSubs(kids).map((k) => (Array.isArray(k) ? subMenu(x, k) : leaf(k, true)))}</div>}
            </div>
          );
        })}
      </div>
      <div className="space-y-0.5 border-t border-rail-line p-2">
        {visible.find((r) => r.path === '/help') && leaf(visible.find((r) => r.path === '/help')!, false)}
        {canInstall && (
          <button type="button" onClick={() => void install()} title={collapsed ? 'Install app' : undefined} className={itemCls(false) + ' w-full'}>
            <Download aria-hidden className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && 'Install app'}
          </button>
        )}
        {onToggleCollapse && (
          <button type="button" onClick={onToggleCollapse} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'} className="hidden min-h-touch w-full items-center gap-3 rounded-tag px-3 text-sm text-rail-muted hover:bg-rail-hover/70 hover:text-rail-ink lg:flex">
            {collapsed ? <ChevronsRight aria-hidden className="h-[18px] w-[18px]" /> : <ChevronsLeft aria-hidden className="h-[18px] w-[18px]" />}
            {!collapsed && 'Collapse'}
          </button>
        )}
        {!collapsed && <p className="px-3 pb-1 pt-2 text-xs text-rail-muted">{BRAND.operator}</p>}
      </div>
    </nav>
  );
}

function SidebarCabinets({ onNavigate }: { onNavigate?: () => void }) {
  const cabinets = useCabinets();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = cabinetFromPath(pathname);
  const selected = params.folderId ? folderNodeId(params.cabinetId!, params.folderId) : params.cabinetId ? cabinetNodeId(params.cabinetId) : null;
  if (cabinets.isLoading) return <p className="py-2 pl-11 text-xs text-rail-muted">Loading cabinets…</p>;
  if (cabinets.isError) return <p className="py-2 pl-11 text-xs text-danger">Could not load cabinets.</p>;
  if (!cabinets.data?.length) return <p className="py-2 pl-11 text-xs text-rail-muted">No cabinets shared with you yet.</p>;
  return (
    <CabinetTree
      dense
      label="Cabinets and folders"
      className="py-1 pl-4"
      cabinets={cabinets.data}
      selected={selected}
      defaultOpen={params.cabinetId ? [cabinetNodeId(params.cabinetId)] : []}
      onSelect={({ cabinetId, folderId }) => {
        navigate(folderId ? `/cabinets/${cabinetId}/f/${folderId}` : `/cabinets/${cabinetId}`);
        onNavigate?.();
      }}
    />
  );
}

/** Group routes by `sub` (kept at the position of the first entry); plain routes stay as they are. */
export function withSubs(rs: RouteDef[]): (RouteDef | RouteDef[])[] {
  const out: (RouteDef | RouteDef[])[] = [];
  const subs = new Map<string, RouteDef[]>();
  for (const r of rs) {
    if (!r.sub) { out.push(r); continue; }
    const g = subs.get(r.sub);
    if (g) g.push(r);
    else { const n = [r]; subs.set(r.sub, n); out.push(n); }
  }
  return out;
}

/** The sidebar sits outside the page's <Route>, so read the cabinet ids from the path. */
function cabinetFromPath(pathname: string): { cabinetId?: string; folderId?: string } {
  const m = /^\/cabinets\/([^/]+)(?:\/f\/([^/]+))?/.exec(pathname);
  return m ? { cabinetId: decodeURIComponent(m[1]), folderId: m[2] ? decodeURIComponent(m[2]) : undefined } : {};
}
